/**
 * Analog product page (sections/main-product-analog.liquid,
 * assets/analog-product.css): the pen's part in the theme's product form.
 *
 * <analog-product> wraps the section, and a quick view's form in the drawer
 * (the theme moves that in from the product page, so it is defined on
 * every page: snippets/era-head.liquid). It gives each option the pen mark
 * that shows it chosen, a circle round a typed choice and a box round a
 * colour, each fitted to its box so the pen keeps one width, with the
 * pencil's faint trace of it under, shown while the choice is hovered, and
 * writes a chosen value into its blank (is-writing) when it changes. The
 * theme re-renders the form's blocks on every variant change and custom.js
 * swaps the whole section for a sibling colour's, so it watches its own
 * subtree and marks whatever arrives unmarked; the marks are drawn on (CSS)
 * as the radio under them is checked. The values it has seen outlast a
 * swap, so the new colour is written in too. A quick view's form is kept
 * unseen until its stylesheet has come. Fails quietly: nothing here throws
 * into the page.
 */

(() => {
  if (customElements.get("analog-product")) return;

  /* Each mark drawn by hand three ways, for a box of one shape; a choice
     takes one by its place in the list, so neighbours differ. */
  const ANALOG_PATHS = {
    circle: {
      box: "0 0 100 40",
      d: [
        "M12 26C7 12 38 3 60 4C86 5 98 13 96 24C94 35 62 39 38 38C14 37 2 30 6 19C9 11 22 7 36 5",
        "M86 8C68 2 26 3 9 14C-1 22 8 35 40 37C70 39 97 33 95 20C94 10 78 4 58 5",
        "M16 31C2 22 12 6 46 4C80 2 99 12 94 26C90 37 50 40 26 36C11 33 7 24 18 14"
      ]
    },
    box: {
      box: "0 0 100 100",
      d: [
        "M10 4C40 2 70 3 95 5C97 35 96 68 94 95C64 97 32 96 5 94C3 64 4 34 6 2",
        "M6 9C4 38 5 68 7 94C37 96 67 95 94 93C96 63 95 33 93 5C63 3 33 4 3 6",
        "M95 10C96 39 95 69 93 95C63 97 33 96 6 94C4 64 5 34 7 5C37 3 67 4 97 7"
      ]
    }
  };

  /* Each option's value as last seen, by the option's name: a sibling
     colour's section has other ids, and a new <analog-product>. */
  const analogValues = new Map();

  /* Two strokes of the one path: under the pen's, a pencil's, which shows
     faintly when a choice is hovered, the circle or box the pen would draw. */
  const analogMark = (kind, n) => {
    const { box, d } = ANALOG_PATHS[kind];
    const path = d[n % d.length];
    const stroke = `pathLength="1" d="${path}" data-d="${path}" data-box="${box}"`;
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", `mark mark--${kind}`);
    svg.setAttribute("viewBox", box);
    svg.setAttribute("aria-hidden", "true");
    svg.innerHTML = `<path class="trace" ${stroke}/><path ${stroke}/>`;
    return svg;
  };

  /* A mark's paths scaled to the box it has on the page. */
  const analogFit = (svg) => {
    const { width, height } = svg.getBoundingClientRect();
    if (!width || !height) return;
    svg.querySelectorAll("path").forEach((path) => {
      const [, , w, h] = path.dataset.box.split(" ").map(Number);
      let i = 0;
      path.setAttribute(
        "d",
        path.dataset.d.replace(/-?\d+(\.\d+)?/g, (n) =>
          (Number(n) * (i++ % 2 ? height / h : width / w)).toFixed(1)
        )
      );
    });
    svg.setAttribute("viewBox", `0 0 ${width.toFixed(1)} ${height.toFixed(1)}`);
  };

  /* Restart the pen writing a blank in. */
  const analogWrite = (blank) => {
    blank.classList.remove("is-writing");
    void blank.offsetWidth;
    blank.classList.add("is-writing");
  };

  /* The theme's re-render replaces the picker as a choice is made, so the
     chosen one's mark arrives already drawn and its transition never plays:
     the pen draws it in instead, from as far as it had got on the mark it
     replaced (a negative delay; one long past is a mark already drawn). */
  const analogRedraw = (a, fresh) => {
    if (!a.change) return;
    const { name, value, at } = a.change;
    const radio = Array.from(
      a.root.querySelectorAll(
        ".block-swatch__radio:checked, .color-swatch__radio:checked"
      )
    ).find((r) => r.name === name && r.value === value);
    const item =
      radio && (radio.closest(".color-swatch") ?? radio.nextElementSibling);
    const mark = item?.querySelector(".mark");
    if (!mark || !fresh.has(mark)) return;
    a.change = null;
    mark.style.setProperty(
      "--drawn",
      `${Math.round(performance.now() - at)}ms`
    );
    mark.classList.add("is-drawing");
    mark.addEventListener(
      "animationend",
      () => {
        mark.classList.remove("is-drawing");
        mark.style.removeProperty("--drawn");
      },
      { once: true }
    );
  };

  /* Marks for whatever arrived unmarked, and the pen for any blank whose
     value has changed since it was last seen. */
  const analogDecorate = (a) => {
    const fresh = new Set();
    a.marks.forEach((mark) => {
      if (mark.isConnected) return;
      a.fitting.unobserve(mark);
      a.marks.delete(mark);
    });
    a.root.querySelectorAll(".block-swatch-list").forEach((list) =>
      list.querySelectorAll(".block-swatch__item").forEach((item, i) => {
        if (item.querySelector(".mark")) return;
        const mark = analogMark("circle", i + list.children.length);
        item.append(mark);
        a.marks.add(mark);
        a.fitting.observe(mark);
        fresh.add(mark);
      })
    );
    a.root.querySelectorAll(".color-swatch").forEach((swatch, i) => {
      if (swatch.querySelector(".mark")) return;
      const mark = analogMark("box", i + 1);
      swatch.append(mark);
      a.marks.add(mark);
      a.fitting.observe(mark);
      fresh.add(mark);
    });
    analogRedraw(a, fresh);
    a.root.querySelectorAll(".product-form__option-value").forEach((blank) => {
      const key = blank.previousElementSibling?.textContent.trim() || blank.id;
      const value = blank.textContent.trim();
      const seen = analogValues.get(key);
      analogValues.set(key, value);
      if (seen !== undefined && seen !== value) analogWrite(blank);
    });
  };

  /* A quick view brings its stylesheet in beside its form (the link in
     sections/main-product-analog.liquid), which the drawer has when it
     slides in: the form waits, unseen on the drawer's paper, until the sheet
     has come, or a second has passed, so it is never seen unprinted. */
  const analogAwaitStyles = (root) => {
    const link = root.querySelector(":scope > link[rel=stylesheet]");
    if (!link || link.sheet) return;
    root.style.visibility = "hidden";
    const show = () => root.style.removeProperty("visibility");
    link.addEventListener("load", show, { once: true });
    link.addEventListener("error", show, { once: true });
    setTimeout(show, 1000);
  };

  const analogSetup = (root) => {
    const a = {
      root,
      marks: new Set(),
      fitting: new ResizeObserver((entries) =>
        entries.forEach((entry) => analogFit(entry.target))
      ),
      frame: 0,
      change: null
    };
    /* The choice just made, and when, for analogRedraw. */
    const choosing = (event) => {
      if (!event.target.matches(".block-swatch__radio, .color-swatch__radio"))
        return;
      const { name, value } = event.target;
      a.change = { name, value, at: performance.now() };
    };
    root.addEventListener("change", choosing);
    /* The marks it adds are changes too: batched to a frame, and each
       pass adds none the second time. */
    const watching = new MutationObserver(() => {
      cancelAnimationFrame(a.frame);
      a.frame = requestAnimationFrame(() => analogDecorate(a));
    });
    analogDecorate(a);
    watching.observe(root, {
      childList: true,
      subtree: true,
      characterData: true
    });
    return () => {
      root.removeEventListener("change", choosing);
      watching.disconnect();
      a.fitting.disconnect();
      cancelAnimationFrame(a.frame);
    };
  };

  class AnalogProduct extends HTMLElement {
    connectedCallback() {
      try {
        analogAwaitStyles(this);
        this.teardown = analogSetup(this);
      } catch (error) {
        console.error("analog-product:", error);
      }
    }

    disconnectedCallback() {
      this.teardown?.();
      this.teardown = null;
    }
  }

  customElements.define("analog-product", AnalogProduct);
})();
